import { useState, useRef, type FormEvent } from 'react';
import { useAppStore } from '@/store/useAppStore';
import { recalculateStore } from '@/store/useAppStore';
import { GradeLevel, Term, CourseType, AGSubject, GradeFormat, SpecialGrade } from '@gpa-scales/shared-types';
import type { Course } from '@gpa-scales/shared-types';
import { InternationalGradeInput } from './InternationalGradeInput';
import { ScrubInput, Select } from 'calc-controls';
import { HelpTip } from './Tooltip';
const EMPTY_COURSE: Omit<Course, 'id'> = {
  name: '',
  gradeLevel: GradeLevel.Freshman,
  term: Term.Fall,
  academicYear: '',
  grade: '',
  gradeFormat: GradeFormat.Letter,
  credits: 1.0,
  courseType: CourseType.Standard,
  agSubject: AGSubject.None,
  isRepeated: false,
  specialGrade: SpecialGrade.None,
};

export function CourseEntryForm() {
  const addCourse = useAppStore((s) => s.addCourse);
  const updateCourse = useAppStore((s) => s.updateCourse);
  const editingCourseId = useAppStore((s) => s.editingCourseId);
  const courses = useAppStore((s) => s.courses);
  const setEditingCourse = useAppStore((s) => s.setEditingCourse);

  const editingCourse = editingCourseId ? courses.find((c) => c.id === editingCourseId) : null;
  const [form, setForm] = useState<Omit<Course, 'id'>>(EMPTY_COURSE);
  const [isCollapsed, setIsCollapsed] = useState(false);

  // When editing, populate form and expand
  const prevEditId = useRef<string | null>(null);
  if (editingCourse && prevEditId.current !== editingCourseId && editingCourseId) {
    const { id: _id, ...rest } = editingCourse;
    setForm(rest);
    prevEditId.current = editingCourseId;
    setIsCollapsed(false);
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.grade.trim()) return;

    if (editingCourseId) {
      updateCourse(editingCourseId, form);
      setEditingCourse(null);
    } else {
      addCourse(form);
    }
    setForm({ ...EMPTY_COURSE });
    recalculateStore();
    setIsCollapsed(true);
  };

  const handleCancel = () => {
    setForm({ ...EMPTY_COURSE });
    setEditingCourse(null);
  };

  const update = (field: string, value: unknown) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <form id="course-entry-form" onSubmit={handleSubmit} className="rounded-lg border border-border bg-surface p-4" data-testid="course-entry-form">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-[var(--color-text)]">
          {editingCourseId ? 'Edit Course' : 'Add Course'}
        </h2>
        <button
          type="button"
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="rounded p-1 text-[var(--color-text-muted)] hover:bg-[var(--color-bg-tertiary)]"
          aria-label={isCollapsed ? 'Expand form' : 'Collapse form'}
        >
          <svg className={`h-4 w-4 transition-transform ${isCollapsed ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>
      {!isCollapsed && (
      <>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <label htmlFor="course-name" className="mb-1 block text-xs text-[var(--color-text-secondary)]">Course Name *</label>
          <input
            type="text"
            id="course-name"
            value={form.name}
            onChange={(e) => update('name', e.target.value)}
            className={`w-full rounded-md border px-3 py-2 text-sm ${
              form.name.trim() === '' && form.grade.trim() !== '' ? 'border-error' : 'border-border'
            }`}
            placeholder="e.g. AP Calculus BC"
            maxLength={120}
            autoComplete="off"
            data-testid="input-course-name"
            required
          />
        </div>
        <div>
          <label htmlFor="grade-level" className="mb-1 block text-xs text-[var(--color-text-secondary)]" title="Only 10th and 11th grade A-G courses count toward UC GPA variants.">Grade Level</label>
          <Select
            inline
            label="Grade Level"
            value={String(form.gradeLevel)}
            onChange={(v) => update('gradeLevel', Number(v))}
            options={[
              { value: String(GradeLevel.Freshman), label: '9th Grade' },
              { value: String(GradeLevel.Sophomore), label: '10th Grade' },
              { value: String(GradeLevel.Junior), label: '11th Grade' },
              { value: String(GradeLevel.Senior), label: '12th Grade' },
            ]}
          />
        </div>
        <div>
          <label htmlFor="course-term" className="mb-1 block text-xs text-[var(--color-text-secondary)]">Term</label>
          <Select
            inline
            label="Term"
            value={form.term}
            onChange={(v) => update('term', v)}
            options={[
              { value: Term.Fall, label: 'Fall' },
              { value: Term.Spring, label: 'Spring' },
              { value: Term.Summer, label: 'Summer' },
            ]}
          />
        </div>
        <div>
          <label htmlFor="academic-year" className="mb-1 block text-xs text-[var(--color-text-secondary)]">Academic Year</label>
          <input
            type="text"
            id="academic-year"
            value={form.academicYear}
            onChange={(e) => update('academicYear', e.target.value)}
            className="w-full rounded-md border border-border px-3 py-2 text-sm"
            placeholder="2025-2026"
            maxLength={9}
            inputMode="numeric"
            autoComplete="off"
            data-testid="input-academic-year"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-[var(--color-text-secondary)]">Grade *</label>
          <InternationalGradeInput
            gradeFormat={form.gradeFormat}
            grade={form.grade}
            onGradeChange={(g) => update('grade', g)}
            onFormatChange={(f) => update('gradeFormat', f)}
          />
        </div>
        <div>
          <label htmlFor="course-type" className="mb-1 block text-xs text-[var(--color-text-secondary)]" title="Determines weighting: AP, IB HL/SL, Honors, and Dual Enrollment earn bonus points.">Course Type</label>
          <Select
            inline
            label="Course Type"
            value={form.courseType}
            onChange={(v) => update('courseType', v)}
            options={[
              { value: CourseType.Standard, label: 'Standard' },
              { value: CourseType.Honors, label: 'Honors' },
              { value: CourseType.AP, label: 'AP' },
              { value: CourseType.IB_HL, label: 'IB HL' },
              { value: CourseType.IB_SL, label: 'IB SL' },
              { value: CourseType.DualEnrollment, label: 'Dual Enrollment' },
              { value: CourseType.College, label: 'College' },
            ]}
          />
        </div>
        <div>
          <div className="mb-1 flex items-center gap-1 text-xs text-[var(--color-text-secondary)]">
            <label htmlFor="ag-subject">A-G Subject</label>
            <HelpTip
              content={'UC\u2019s college-preparatory categories. Only A-G courses taken in 10th\u201311th grade count toward the UC GPA variants.'}
              label="About A-G subject"
              placement="bottom"
            />
          </div>
          <Select
            inline
            label="A-G Subject"
            value={form.agSubject}
            onChange={(v) => update('agSubject', v)}
            options={[
              { value: AGSubject.None, label: 'Not A-G' },
              { value: AGSubject.A, label: 'A — History/Social Science' },
              { value: AGSubject.B, label: 'B — English' },
              { value: AGSubject.C, label: 'C — Mathematics' },
              { value: AGSubject.D, label: 'D — Laboratory Science' },
              { value: AGSubject.E, label: 'E — Language' },
              { value: AGSubject.F, label: 'F — Visual & Performing Arts' },
              { value: AGSubject.G, label: 'G — College-Prep Elective' },
            ]}
          />
        </div>
        <div>
          <label htmlFor="course-credits" className="mb-1 block text-xs text-[var(--color-text-secondary)]">Credits</label>
          <ScrubInput
            value={form.credits}
            onChange={(v) => update('credits', v)}
            label="Credits"
            unit="cr"
            min={0.5}
            max={10}
            step={0.5}
            decimals={1}
            inline
          />
        </div>
      </div>
      <div className="mt-3 flex items-center gap-2">
        {[CourseType.Honors, CourseType.IB_SL, CourseType.DualEnrollment].includes(form.courseType) && (
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 text-xs text-[var(--color-text-secondary)]">
              <input
                type="checkbox"
                checked={form.isUCCertifiedHonors ?? false}
                onChange={(e) => update('isUCCertifiedHonors', e.target.checked)}
                className="h-4 w-4 rounded border-border"
              />
              UC-Certified Honors
            </label>
            <HelpTip
              content={'Mark if this course is on your school\u2019s UC-approved honors list. Only UC-certified Honors, IB SL, and Dual Enrollment earn the UC +1 bonus; AP and IB HL are always eligible.'}
              label="About UC-certified honors"
              placement="bottom"
            />
          </div>
        )}
        <div className="flex-1" />
        <button
          type="button"
          onClick={handleCancel}
          className="rounded-md px-4 py-2 text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)]"
          data-testid="btn-cancel-course"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-hover"
          data-testid="btn-submit-course"
        >
          {editingCourseId ? 'Update' : 'Add Course'}
        </button>
      </div>
      </>
      )}
    </form>
  );
}
